import { ArrowLeft, BookOpenText, FolderKanban, Inbox, ListTodo, type LucideIcon, Search, Settings, Shield, Star } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useUnreadCount } from '../api/inbox';
import { useProjectMap } from '../api/projects';
import { Counter } from '../components/ui/Badge';
import { useTooltip } from '../components/ui/Tooltip';
import { cn } from '../lib/cn';
import { ADMIN_MODULES, adminGroupsFor, isAdminPath } from './adminNav';
import { useVisibleModules } from './modules';
import { useFavoriteProjects, useRecentProjects } from './prefs';
import { useSession } from './session';

function NavItem({
  to,
  icon: Icon,
  label,
  end,
  badge,
  badgeHint,
  onNavigate,
  tour,
  collapsed,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
  badge?: ReactNode;
  /** Опис лічильника для згорнутої панелі (там замість числа — крапка на значку). */
  badgeHint?: string;
  onNavigate?: () => void;
  /** Мітка для навчального туру (data-tour). */
  tour?: string;
  /** Згорнута панель: лише значок, назва — у підказці праворуч. */
  collapsed?: boolean;
}) {
  const name = collapsed && badge && badgeHint ? `${label} — ${badgeHint}` : label;
  const tip = useTooltip(name, { placement: 'right-start', delay: 200, describe: false, disabled: !collapsed });
  return (
    <>
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        data-tour={tour}
        aria-label={collapsed ? name : undefined}
        {...(collapsed ? tip.triggerProps : {})}
        className={({ isActive }) =>
          cn(
            'group relative flex h-8 items-center rounded-sm text-body transition-colors',
            collapsed ? 'w-9 shrink-0 justify-center' : 'gap-2.5 px-2.5',
            isActive ? 'bg-accent-soft font-medium text-accent-fg' : 'text-fg-2 hover:bg-surface-3 hover:text-fg',
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon className={cn('size-[18px] shrink-0', isActive ? 'text-accent-fg' : 'text-fg-3 group-hover:text-fg-2')} aria-hidden strokeWidth={1.75} />
            {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
            {collapsed ? badge ? <span className="absolute top-1 right-1 size-2 rounded-full bg-accent ring-2 ring-canvas" aria-hidden /> : null : badge}
          </>
        )}
      </NavLink>
      {tip.element}
    </>
  );
}

function ProjectLink({ id, name, onNavigate, fav }: { id: string; name: string; onNavigate?: () => void; fav?: boolean }) {
  return (
    <NavLink
      to={`/projects/${id}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex h-7 items-center gap-2.5 rounded-sm px-2.5 text-meta transition-colors',
          isActive ? 'bg-accent-soft font-medium text-accent-fg' : 'text-fg-2 hover:bg-surface-3 hover:text-fg',
        )
      }
    >
      {fav ? (
        <Star className="size-3.5 shrink-0 fill-current text-fg-3" aria-hidden />
      ) : (
        <span className="flex size-3.5 shrink-0 items-center justify-center" aria-hidden>
          <span className="size-1.5 rounded-full bg-line-input" />
        </span>
      )}
      <span className="truncate">{name}</span>
    </NavLink>
  );
}

/**
 * Бічна навігація: основні розділи, модулі (коли мають сторінки), обрані й останні проєкти.
 * Розгорнута — завширшки за найдовшою назвою розділу (назви проєктів і організації ширину не збільшують);
 * згорнута (collapsed) — вузька смуга значків із назвами в підказках.
 */
export function SidebarNav({ onNavigate, collapsed = false }: { onNavigate?: () => void; collapsed?: boolean }) {
  const { can, isAdmin, isOwner, me } = useSession();
  const { pathname } = useLocation();
  const unread = useUnreadCount();
  const projects = useProjectMap();
  const fav = useFavoriteProjects();
  const recent = useRecentProjects();
  const allModules = useVisibleModules(can);
  // Адміністративна частина — окреме меню: щойно адміністратор у ній, бічна панель показує її розділи.
  const adminMode = isAdmin && isAdminPath(pathname);
  const adminGroups = adminGroupsFor({ isOwner, hasModule: (key) => allModules.some((m) => m.key === key) });
  // Модулі налаштування системи адміністраторові в робочому меню не потрібні — вони в адміністративній частині.
  const modules = isAdmin ? allModules.filter((m) => !ADMIN_MODULES.has(m.key)) : allModules;
  const favList = fav.ids.map((id) => projects.get(id)).filter((p) => p && !p.archivedAt);
  const recentList = recent.ids
    .filter((id) => !fav.ids.includes(id))
    .map((id) => projects.get(id))
    .filter((p) => p && !p.archivedAt)
    .slice(0, 5);
  const group = cn('flex flex-col gap-0.5', collapsed && 'items-center');
  const divider = collapsed ? <span className="mx-auto my-1 block h-px w-6 bg-line" aria-hidden /> : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Link
        to="/my-work"
        onClick={onNavigate}
        aria-label="Єдина робоча система — до «Моєї роботи»"
        title="До «Моєї роботи»"
        className={cn('flex h-[var(--topbar-h)] shrink-0 items-center outline-none hover:bg-surface-3 focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset', collapsed ? 'justify-center' : 'gap-2.5 px-4')}
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-sm bg-accent text-body font-semibold text-on-accent" aria-hidden>
          Є
        </span>
        {!collapsed && (
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-body font-semibold text-fg">Єдина робоча система</span>
            <span className="block truncate text-caption text-fg-3 [contain:inline-size]">{adminMode ? 'Адміністрування порталу' : me.organization.name}</span>
          </span>
        )}
      </Link>
      {adminMode ? (
        <nav aria-label="Адміністрування порталу" data-tour="admin-nav" className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto pt-1 pb-4', collapsed ? 'gap-2 px-2' : 'gap-4 px-2.5 [scrollbar-gutter:stable]')}>
          <div className={group}>
            <NavItem to="/my-work" icon={ArrowLeft} label="До роботи" onNavigate={onNavigate} collapsed={collapsed} />
          </div>
          {adminGroups.map((g) => (
            <div key={g.label} className={group}>
              {collapsed ? divider : <p className="px-2.5 pb-1 text-caption font-medium text-fg-3">{g.label}</p>}
              {g.items.map((i) => (
                <NavItem key={i.to} to={i.to} icon={i.icon} label={i.label} onNavigate={onNavigate} collapsed={collapsed} />
              ))}
            </div>
          ))}
        </nav>
      ) : (
      <nav
        aria-label="Основна навігація"
        className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto pt-1 pb-4', collapsed ? 'gap-2 px-2' : 'gap-5 px-2.5 [scrollbar-gutter:stable]')}
      >
        <div className={group}>
          <NavItem to="/my-work" icon={ListTodo} label="Моя робота" onNavigate={onNavigate} tour="nav-my-work" collapsed={collapsed} />
          <NavItem
            to="/inbox"
            icon={Inbox}
            label="Вхідні"
            onNavigate={onNavigate}
            tour="nav-inbox"
            collapsed={collapsed}
            badge={unread.data ? <Counter value={unread.data} tone="accent" label={`Непрочитаних: ${unread.data}`} /> : null}
            badgeHint={unread.data ? `непрочитаних ${unread.data}` : undefined}
          />
          <NavItem to="/projects" icon={FolderKanban} label="Проєкти" end onNavigate={onNavigate} tour="nav-projects" collapsed={collapsed} />
          <NavItem to="/search" icon={Search} label="Пошук" onNavigate={onNavigate} tour="nav-search" collapsed={collapsed} />
          <NavItem to="/knowledge/terms" icon={BookOpenText} label="Довідник понять" onNavigate={onNavigate} tour="nav-knowledge" collapsed={collapsed} />
        </div>

        {modules.length > 0 && (
          <div className={group}>
            {collapsed ? divider : <p className="px-2.5 pb-1 text-caption font-medium text-fg-3">Модулі</p>}
            {modules.flatMap((m) =>
              (m.nav ?? [{ to: m.basePath, label: m.label, icon: m.icon }]).map((n) => (
                <NavItem key={n.to} to={n.to} icon={n.icon ?? m.icon} label={n.label} end={n.end} onNavigate={onNavigate} collapsed={collapsed} />
              )),
            )}
          </div>
        )}

        {/* Назви проєктів ширину панелі не збільшують (contain: inline-size) — скорочуються трикрапкою. */}
        {!collapsed && favList.length > 0 && (
          <div className="flex flex-col gap-0.5 [contain:inline-size]">
            <p className="px-2.5 pb-1 text-caption font-medium text-fg-3">Обрані проєкти</p>
            {favList.map((p) => (
              <ProjectLink key={p!.id} id={p!.id} name={p!.name} onNavigate={onNavigate} fav />
            ))}
          </div>
        )}
        {!collapsed && recentList.length > 0 && (
          <div className="flex flex-col gap-0.5 [contain:inline-size]">
            <p className="px-2.5 pb-1 text-caption font-medium text-fg-3">Останні</p>
            {recentList.map((p) => (
              <ProjectLink key={p!.id} id={p!.id} name={p!.name} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </nav>
      )}
      <div className={cn('flex shrink-0 flex-col gap-0.5 border-t border-line py-2.5', collapsed ? 'items-center px-2' : 'px-2.5')}>
        {isAdmin && !adminMode && <NavItem to="/admin" icon={Shield} label="Адміністрування порталу" onNavigate={onNavigate} collapsed={collapsed} />}
        <NavItem to="/settings" icon={Settings} label="Мої налаштування" onNavigate={onNavigate} collapsed={collapsed} />
      </div>
    </div>
  );
}
